// /////////////////////////////////////////////////////////////
// 🧹インポート
// /////////////////////////////////////////////////////////////

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Portal,
  InputAdornment,
  IconButton,
  Chip,
  Switch,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from "../constants/icons";
import { PAGE_FILL_HEIGHT } from '../constants/layout';
import { db } from "../firebase/firebase";
import {
  collection,
  onSnapshot,
  query,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  orderBy
} from "firebase/firestore";
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { ep } from '../api/endpoints';
import { getIdToken } from '../firebase/getIdToken';
import LoadingView from '../components/LoadingView';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';

// ----- @dnd-kit のインポート -----
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type DragCancelEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


// /////////////////////////////////////////////////////////////
// ⚙️ 定義・インターフェース・props
// /////////////////////////////////////////////////////////////

// --------------------------------------------------
// Permission データモデル
interface Permission {
  id: string;
  name: string;
}

// 権限カードが所属できる2つの列（付与済み / 未割当）
type ContainerKey = 'granted' | 'available';

// --------------------------------------------------
// HozenallCloud_Roles_Permissions のドキュメント（親 + child、型上は再帰的にネスト可能）を
// フラットな Permission[] に変換するための純粋関数。
// permission を持たない「子をまとめるだけの親（カテゴリ）」もあり得るため、
// ここでは permission / child を optional として緩く定義する。
type RolesPermissionNode = {
  label?: string;
  permission?: string;
  child?: unknown;
};

// child は配列で来る場合とオブジェクト（マップ、キーは任意）で来る場合があるため吸収する
// （NavigationBar.tsx の子メニュー正規化と同じ考え方。用途が異なるためここに閉じて実装する）
const normalizeRolesPermissionChildren = (child: unknown): RolesPermissionNode[] => {
  if (!child) return [];
  try {
    if (Array.isArray(child)) {
      return child.filter((c) => c !== null && c !== undefined);
    }
    if (typeof child === 'object') {
      return Object.values(child as Record<string, unknown>).filter(
        (c) => c !== null && c !== undefined
      ) as RolesPermissionNode[];
    }
  } catch (e) {
    console.error('権限マスタの子データ正規化に失敗しました:', e);
  }
  return [];
};

// 1件のドキュメント（親 or 子）を再帰的に Permission[] へ展開する。
// - 自身が permission 文字列を持つ場合のみカード化する
//   （持たない場合は子をまとめるだけのコンテナとみなし、doc.id 由来の「幽霊カード」は作らない）
// - name は「親ラベル > 自身のラベル」の breadcrumb 形式にする（最上位はラベルのみ）
const flattenRolesPermissionNode = (
  node: RolesPermissionNode,
  parentLabel?: string
): Permission[] => {
  const label = node.label || '名称未設定';
  const displayName = parentLabel ? `${parentLabel} > ${label}` : label;

  const result: Permission[] = [];
  if (node.permission) {
    result.push({ id: node.permission, name: displayName });
  }

  normalizeRolesPermissionChildren(node.child).forEach((child) => {
    result.push(...flattenRolesPermissionNode(child, displayName));
  });

  return result;
};

// --------------------------------------------------
// 権限文字列（例 "menu.crmcustomermanagement.view"）をクリックだけで組み立てるための候補一覧。
// 配列に到達した階層がその枝の終端（＝そこで権限文字列が完成する）。
// 段数は固定ではなく、object のままさらに深く枝分かれさせることもできる
// （例 "menu.development.isodocuments.view" のような4段構成）。
// 新しい画面・操作を追加する場合はここに追記する。
type PermissionNode = readonly string[] | { readonly [key: string]: PermissionNode };

const PERMISSION_CANDIDATES: Record<string, PermissionNode> = {
  menu: {
    crmcustomermanagement: ['view', 'edit', 'delete'],
    salesprojectsmanagement: ['view', 'edit', 'delete'],
    salesprojectscreate: ['view', 'edit'],
    salesprojectsfilters: ['view', 'edit'],
    workflow: ['view', 'edit'],
    hozenallnetprojectsmanagement: ['view', 'edit'],
    obcdatasync: ['view', 'edit'],
    obcmastermanagement: ['view', 'edit'],
    monthlytrialbalance: ['view', 'edit'],
    profitandlossstatementsales: ['view'],
    profitandlossstatementmanagement: ['view', 'edit'],
    turquoisetorihikisaki: ['view', 'edit'],
    turquoisematchingreview: ['view', 'edit'],
    googleworkspace: ['view'],
    touki: ['view', 'edit'],
    masterviewmanagement: ['view', 'edit'],
    development: {
      isodocuments: ['view', 'edit'],
      aiplans: ['view', 'edit'],
    },
  },
};

// --------------------------------------------------
// カード UI – 並び替え可能コンポーネントで使用
const cardSx = {
  bgcolor: 'rgba(255,255,255,0.18)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 2,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  // 一覧のスクロール量を抑えるため、上下パディングは左右より詰める
  px: 2,
  py: 1,
  transition: 'transform .2s, box-shadow .2s',
  // ホバー演出（浮き上がり）はカード全体ではなく、左端のドラッグハンドル
  // （6点リーダー）にマウスが乗ったときだけ反応させる
  '&:has(.drag-handle:hover)': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
  },
  '&:has(.drag-handle:active)': {
    opacity: 0.6,
  },
};

// --------------------------------------------------
// 並び替え可能な Permission カード（従来の HTML5 ドラッグ実装を置き換え）
// useSortable は @dnd-kit/sortable というライブラリによって提供されており、
// 初期座標を記憶して移動座標を計算する。
// --------------------------------------------------
const PermissionSortableCard = ({ permission }: { permission: Permission }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: permission.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // ドラッグ中は元のカードを完全に隠す（見た目は DragOverlay 側のコピーが表示を担う）
    opacity: isDragging ? 0 : 1,
    // カーソルはカード全体では変えず、ドラッグハンドル側だけ grab にする
  };
  return (
    <Paper ref={setNodeRef} style={style} sx={{ ...cardSx, display: 'flex', alignItems: 'center', mb: 0.75 }}>
      {/* ドラッグハンドル：Tooltip に "ドラッグで並び替え" を表示 */}
      <Tooltip title="ドラッグで並び替え">
        {/* DnD の属性とリスナーを展開し、ハンドルとして機能させる */}
        <Box
          {...attributes}
          {...listeners}
          className="drag-handle"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.disabled', // アイコンの色を薄く表示
            cursor: 'grab', // デフォルトは掴める手形カーソル
            touchAction: 'none', // タッチデバイスでスクロール防止
            '&:active': { cursor: 'grabbing' }, // ドラッグ中は握る手形に変更
            mr: 1, // アイコンとテキストの間に余白
          }}
        >
          {/* ドラッグ用アイコン */}
          <Icons.DragIndicator sx={{ fontSize: 18 }} />
        </Box>
      </Tooltip>
      {/* 権限名テキスト */}
      <Typography variant="body2">{permission.name}</Typography>
    </Paper>
  );
};

// --------------------------------------------------
// ドロップ対象エリア（付与済み・利用可能の 2 カラム）
const DroppableArea = ({ id, children }: { id: string; children: React.ReactNode }) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box ref={setNodeRef} sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1 }}>
      {children}
    </Box>
  );
};

// --------------------------------------------------
// 社員検索ボックス。入力状態をローカルに閉じ込め、React.memo で親から分離する。
// 検索 state を親（ページ全体）に直接置くと、1打鍵ごとに社員テーブル全行と
// 右側パネルまで再描画されカクつくため、入力停止後 250ms（または Enter で即時）に
// のみ onSearch で親へ確定値を通知する。
const StaffSearchBox = React.memo(function StaffSearchBox({ onSearch }: { onSearch: (word: string) => void }) {
  const [value, setValue] = useState('');
  const timerRef = useRef<number | undefined>(undefined);

  // アンマウント時に未発火の debounce タイマーを破棄する
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  // 入力停止を少し待ってから親へ通知する（IME 変換中の連続 onChange もここで吸収される）
  const notifyLater = (next: string) => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => onSearch(next), 250);
  };

  // Enter・クリア時は待たずに即時反映する
  const notifyNow = (next: string) => {
    window.clearTimeout(timerRef.current);
    onSearch(next);
  };

  return (
    <TextField
      size="small"
      placeholder="お名前、部署、IDで検索..."
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        notifyLater(e.target.value);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') notifyNow(value); }}
      InputProps={{
        startAdornment: <Icons.Search sx={{ color: 'text.secondary', mr: 1 }} />,
        // 入力があるときだけ × を表示し、クリックで検索文字列をリセットする
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => { setValue(''); notifyNow(''); }} aria-label="検索条件をクリア">
              <Icons.Clear fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{ flex: 1, minWidth: 140, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    />
  );
});

// --------------------------------------------------
// StandardDataTable（memo 化済み）へ渡す何もしないハンドラ。
// inline の () => {} は毎レンダー新規参照になり memo を無効化するため、モジュール定数にする。
const noopTableHandler = () => { };

// --------------------------------------------------
// Firestore / DB のインターフェイス
export interface PermissionDoc {
  id: string;
  user_id: string;
  permissions: string[];
}

export interface DbStaff {
  staff_id: string;
  staff_name: string;
  department_name?: string;
  branch_name?: string;
  role_name?: string;
  joinedcompany_date?: string | null;
  retirement_date?: string | null;
}

export type UserPermissionDoc = PermissionDoc & {
  name?: string;
  department?: string;
  branch?: string;
  joinedcompany_date?: string | null;
  retirement_date?: string | null;
  role?: string;
};

// --------------------------------------------------
// AI参照スコープ管理タブ（保存先は Cloud SQL の mst_ai_scope_grants。
// 画面メニュー権限の Firestore とは独立した「AIのDB参照範囲」の付与管理）
interface AiScopeInfo {
  scope: string;        // スコープ名 (starter / personnel など)
  schema: string;       // 対応する ai_* スキーマ名
  description: string;  // スキーマコメント (何が見えるかの説明)
}

interface AiScopeGrant {
  user_id: string;
  scope: string;
  granted_by: string;
  granted_at: string;
}

// スコープ表示名（カタログに無い将来スコープはスコープ名をそのまま表示）
const AI_SCOPE_LABELS: Record<string, string> = {
  starter: 'スターター（基本情報）',
  personnel: 'パーソナル（人事情報）',
  obc: '勘定奉行（会計情報）',
  event: 'イベント（案内・申込管理）',
  crm: '顧客管理（顧客・建物情報）',
  attendance: '勤怠管理（出退勤・休暇申請）',
  turquoise: 'ターコイズ（取引先情報）',
  infasst: 'アカウント資産',
};

// スコープ定義の閲覧（フェーズA・読み取り専用）とカラム構成編集（フェーズB）
interface AiViewColumn {
  name: string;
  type: string;
  description: string;
  permanent?: boolean;  // 恒久非公開 (サーバー側デナイリスト。編集でも公開不可)
}

// 参照元テーブルの全カラム（編集チェックリストの母集団。定義順）
interface AiBaseTableColumn extends AiViewColumn {
  exposed: boolean;     // 現在ビューに公開されているか
  permanent: boolean;
}

interface AiBaseTable {
  table: string;
  hidden_columns: AiViewColumn[];
  all_columns?: AiBaseTableColumn[];
}

interface AiViewDefinition {
  view: string;
  description: string;
  definition: string;   // 稼働中のビュー定義 (pg_get_viewdef の現物)
  columns: AiViewColumn[];
  base_tables: AiBaseTable[];
  editable?: boolean;   // 単一テーブル参照ビューのみ画面から編集可
}

interface AiScopeDefinition {
  scope: string;
  schema: string;
  description: string;
  views: AiViewDefinition[];
}

// カラム構成の変更履歴（trn_ai_view_change_history）
interface AiViewChange {
  view: string;
  changed_by: string;
  changed_at: string;
  added: string;
  removed: string;
}

// --------------------------------------------------
// AIビューの公開カラム編集（フェーズB・構造化編集）。
// チェック変更 → SQLプレビュー生成（サーバー側で検証・生成）→ 確認 → 適用の3段階。
// WHERE/FROM 句は現行定義から自動で引き継がれ、画面からは編集できない。
// 恒久非公開カラム（パスワード類・システム管理列）はサーバー側デナイリストで強制され、
// 画面でもロック表示で選択不可にしている。
const AiViewColumnEditor = React.memo(function AiViewColumnEditor({
  def,
  onCancel,
  onApplied,
}: {
  def: AiViewDefinition;
  onCancel: () => void;
  onApplied: () => void;
}) {
  const notify = useNotification();
  const confirm = useConfirm();
  const allColumns = def.base_tables[0]?.all_columns ?? [];
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(def.columns.map((c) => c.name))
  );
  const [preview, setPreview] = useState<{ sql: string; added: string[]; removed: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  // 選択を変えたら生成済みプレビューは無効化する（適用は必ず最新プレビュー経由）
  const toggleColumn = (name: string) => {
    setPreview(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const exposedNow = React.useMemo(() => new Set(def.columns.map((c) => c.name)), [def.columns]);
  const addedNames = allColumns.filter((c) => selected.has(c.name) && !exposedNow.has(c.name)).map((c) => c.name);
  const removedNames = allColumns.filter((c) => !selected.has(c.name) && exposedNow.has(c.name)).map((c) => c.name);
  const hasChange = addedNames.length > 0 || removedNames.length > 0;

  const handlePreview = async () => {
    setBusy(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        ep('ai_scope_view_columns'),
        { view: def.view, columns: Array.from(selected), apply: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        setPreview({ sql: res.data.sql, added: res.data.added || [], removed: res.data.removed || [] });
      } else {
        notify.error(res.data.message || 'SQLプレビューの生成に失敗しました。');
      }
    } catch (err: any) {
      notify.error(err?.response?.data?.message || 'SQLプレビューの生成に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!preview) return;
    const ok = await confirm({
      title: 'AI参照ビューの変更',
      message:
        `${def.view} の公開カラムを変更します（追加 ${preview.added.length} 列 / 削除 ${preview.removed.length} 列）。` +
        '適用すると AI の参照範囲が即時に切り替わります。よろしいですか？',
      confirmText: '適用する',
      destructive: preview.removed.length > 0,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        ep('ai_scope_view_columns'),
        { view: def.view, columns: Array.from(selected), apply: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        notify.success(`${def.view} のカラム構成を変更しました。`);
        onApplied();
      } else {
        notify.error(res.data.message || 'カラム構成の変更に失敗しました。');
      }
    } catch (err: any) {
      notify.error(err?.response?.data?.message || 'カラム構成の変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        公開カラムの構成を直接変更します。WHERE 句・FROM 句は現行定義から自動で引き継がれ、
        ここからは変更できません。ロック表示のカラムは恒久非公開（サーバー側で強制）です。
      </Alert>

      {/* カラムのチェックリスト（参照元テーブルの定義順） */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 320, overflowY: 'auto', mb: 1.5 }}>
        {allColumns.map((c, i) => (
          <Box
            key={c.name}
            onClick={() => !c.permanent && !busy && toggleColumn(c.name)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.5,
              py: 0,
              bgcolor: i % 2 === 0 ? 'background.paper' : 'rgba(0,0,0,0.02)',
              cursor: c.permanent ? 'not-allowed' : 'pointer',
              opacity: c.permanent ? 0.55 : 1,
              '&:hover': c.permanent ? {} : { bgcolor: 'rgba(25,118,210,0.06)' },
            }}
          >
            <Checkbox
              size="small"
              checked={selected.has(c.name)}
              disabled={c.permanent || busy}
              sx={{ p: 0.5 }}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', width: 200, flexShrink: 0 }} noWrap>
              {c.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ width: 110, flexShrink: 0 }} noWrap>
              {c.type}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flexGrow: 1 }}>
              {c.description}
            </Typography>
            {c.permanent && (
              <Tooltip title="恒久非公開（パスワード類・システム管理列。公開できません）">
                <Icons.Lock sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5 }} />
              </Tooltip>
            )}
          </Box>
        ))}
      </Box>

      {/* 差分サマリー */}
      {hasChange && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          {addedNames.map((n) => (
            <Chip key={`a-${n}`} label={`+ ${n}`} size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '10px', fontFamily: 'monospace' }} />
          ))}
          {removedNames.map((n) => (
            <Chip key={`r-${n}`} label={`− ${n}`} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '10px', fontFamily: 'monospace' }} />
          ))}
        </Box>
      )}

      {/* 生成SQLプレビュー（サーバー側で生成した実行予定の現物） */}
      {preview && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[600], display: 'block', mb: 0.5 }}>
            実行される SQL（プレビュー）
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: '#263238',
              color: '#eceff1',
              borderRadius: 1,
              fontSize: '0.72rem',
              fontFamily: 'monospace',
              lineHeight: 1.5,
              maxHeight: 260,
              overflow: 'auto',
              whiteSpace: 'pre',
            }}
          >
            {preview.sql}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Button onClick={onCancel} disabled={busy} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!hasChange || busy}
          onClick={() => void handlePreview()}
          startIcon={<Icons.Description sx={{ fontSize: 16 }} />}
        >
          SQLプレビュー
        </Button>
        <Button
          disabled={!preview || busy}
          onClick={() => void handleApply()}
          startIcon={<Icons.Save />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          適用する
        </Button>
      </Box>
    </Box>
  );
});

// /////////////////////////////////////////////////////////////
// 📡 コンポーネント
// /////////////////////////////////////////////////////////////

export default function MasterViewManagement() {
  const notify = useNotification();
  const confirm = useConfirm();
  const [dbStaffList, setDbStaffList] = useState<DbStaff[]>([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [firestoreUsers, setFirestoreUsers] = useState<UserPermissionDoc[]>([]);
  const [firestoreLoading, setFirestoreLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [registeringId, setRegisteringId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0: 社員・権限管理(統合), 1: 権限マスタ編集
  // 社員・権限管理タブは「手順1: 左リスト(DBスタッフ全件)で社員を1回選択 →
  // 手順2: 右コンテンツ上部のタブで管理対象(画面権限/AIスコープ)を切替」の2段構成。
  // contentTab: 右コンテンツの管理対象 (0: 画面権限編集, 1: AI参照スコープ編集)
  const [contentTab, setContentTab] = useState(0);
  // 左リストに退職者 (retirement_date あり) を含めるか。既定は非表示
  const [showRetired, setShowRetired] = useState(false);
  const [allPermissionsState, setAllPermissionsState] = useState<Permission[]>([]);

  // 付与済み/未割当の「見た目上の」所属列（id の配列）。
  // Firestore の確定値は selectedUser.permissions 側にあり、
  // これはドラッグ操作・表示のためのローカルコピー（楽観的更新用）。
  const [containerItems, setContainerItems] = useState<Record<ContainerKey, string[]>>({
    granted: [],
    available: [],
  });

  // 現在ドラッグ中のカード（DragOverlay に表示する内容）。ドラッグ中でなければ null
  const [activeDragPermission, setActiveDragPermission] = useState<Permission | null>(null);

  // ---- 権限マスタ編集タブ：クリックで権限文字列を組み立てる ----
  const [newPermissionPath, setNewPermissionPath] = useState<string[]>([]);

  // ---- AI参照スコープタブ（保存先は Cloud SQL、操作は scope_admin 保有者のみ）----
  const [aiScopes, setAiScopes] = useState<AiScopeInfo[]>([]);
  const [aiGrants, setAiGrants] = useState<AiScopeGrant[]>([]);
  const [aiScopeLoaded, setAiScopeLoaded] = useState(false);
  const [aiScopeLoading, setAiScopeLoading] = useState(false);
  const [aiScopeForbidden, setAiScopeForbidden] = useState(false);
  const [aiMutatingScope, setAiMutatingScope] = useState<string | null>(null);
  // スコープ定義の閲覧（社員未選択時に右ペインへ常時表示。タブ初回表示時に取得しキャッシュする）
  const [aiDefs, setAiDefs] = useState<AiScopeDefinition[] | null>(null);
  const [aiDefsLoading, setAiDefsLoading] = useState(false);
  // カラム構成の編集モード（対象ビュー名。null なら閲覧表示）と変更履歴
  const [aiEditingView, setAiEditingView] = useState<string | null>(null);
  const [aiHistory, setAiHistory] = useState<AiViewChange[] | null>(null);


  // --------------------------------------------------
  // データ取得（元コードと同様）

  // DB のスタッフ情報（退職者含む）
  useEffect(() => {
    const fetchDbStaff = async () => {
      try {
        const res = await axios.post(ep('get_staff_list'), { include_retired: true });
        if (res.data.status === 'success') {
          const list: DbStaff[] = (res.data.staffList || []).map((s: any) => ({
            ...s,
            role_name: s.role_name ?? "",
            joinedcompany_date: s.joinedcompany_date ?? null,
          }));
          setDbStaffList(list);
        }
      } catch (err) {
        console.error('Error fetching db staff list with retired:', err);
        notify.error('社員一覧の取得に失敗しました。');
      } finally {
        setDbLoading(false);
      }
    };
    fetchDbStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify は NotificationProvider 側で毎レンダー再生成されるため依存配列には含めない
  }, []);

  // Firestore のリアルタイムユーザー情報
  useEffect(() => {
    const q = query(collection(db, "HozenallCloud_Permissions"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        } as UserPermissionDoc));
        setFirestoreUsers(data);
        setFirestoreLoading(false);
      },
      (error) => {
        console.error('firestore error:', error);
        setFirestoreLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Firestoreの「HozenallCloud_Roles_Permissions」から権限マスタをリアルタイム取得
  useEffect(() => {
    const q = query(
      collection(db, "HozenallCloud_Roles_Permissions"),
      orderBy("sort_no", "asc")
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // 親ドキュメント自身の permission に加え、child 配下（再帰的）の permission も展開する
        const fetched = snapshot.docs.flatMap((doc) =>
          flattenRolesPermissionNode(doc.data())
        );

        // 同じ permission 文字列が複数ノードに紐づいてしまった場合の保険として、
        // id（permission 文字列）で重複排除する（先勝ち）
        const deduped = new Map<string, Permission>();
        fetched.forEach((p) => {
          if (!deduped.has(p.id)) deduped.set(p.id, p);
        });

        setAllPermissionsState(Array.from(deduped.values()));
      },
      (error) => {
        console.error("Firestoreから権限マスタの取得に失敗しました:", error);
      }
    );
    return () => unsubscribe();
  }, []);

  // --------------------------------------------------
  // AI参照スコープタブ：一覧取得と付与・剥奪
  // 保存先は Cloud SQL の mst_ai_scope_grants。取得はタブ初回表示時、
  // 付与・剥奪の成功後は全件を再取得して確定値へ同期する。
  const fetchAiScopeOverview = React.useCallback(async () => {
    setAiScopeLoading(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        ep('ai_scope_admin_overview'),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        setAiScopes(res.data.scopes || []);
        setAiGrants(res.data.grants || []);
        setAiScopeForbidden(false);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        // scope_admin 未保有。タブ自体は表示し、案内メッセージへ切り替える
        setAiScopeForbidden(true);
      } else {
        console.error('AI参照スコープの取得に失敗しました', err);
        notify.error('AI参照スコープの取得に失敗しました。');
      }
    } finally {
      setAiScopeLoading(false);
      setAiScopeLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify は NotificationProvider 側で毎レンダー再生成されるため依存配列には含めない
  }, []);

  // 左リストのスコープチップ表示に使うため、社員・権限管理タブの表示時点で取得する
  useEffect(() => {
    if (activeTab === 0 && !aiScopeLoaded) void fetchAiScopeOverview();
  }, [activeTab, aiScopeLoaded, fetchAiScopeOverview]);

  // ユーザー別の付与済みスコープ（一覧チップ・スイッチ状態の参照元）
  const aiGrantsByUser = React.useMemo(() => {
    const map = new Map<string, AiScopeGrant[]>();
    aiGrants.forEach((g) => {
      const list = map.get(g.user_id) ?? [];
      list.push(g);
      map.set(g.user_id, list);
    });
    return map;
  }, [aiGrants]);

  // 左リストの母集団は DB スタッフ全件（Firestore 登録の有無とは独立）。
  // 退職者はトグル (showRetired) が ON のときだけ含める
  const aiFilteredStaff = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (dbStaffList ?? []).filter((s) => {
      if (!s.staff_id) return false;
      if (!showRetired && s.retirement_date) return false;
      if (!q) return true;
      return [s.staff_name, s.department_name, s.branch_name, s.staff_id].some((v) =>
        (v ?? '').toLowerCase().includes(q)
      );
    });
  }, [dbStaffList, searchQuery, showRetired]);

  // 社員の選択も画面権限側と共有 (selectedUserId = mst_account.user_id = staff_id)
  const aiSelectedStaff = React.useMemo(
    () =>
      selectedUserId
        ? dbStaffList.find((s) => s.staff_id === selectedUserId) ?? null
        : null,
    [selectedUserId, dbStaffList]
  );

  // スコープ定義（ビュー定義・公開/非公開カラム）の閲覧。タブ初回表示時に取得してキャッシュする
  const fetchAiDefs = React.useCallback(async () => {
    setAiDefsLoading(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        ep('ai_scope_view_definitions'),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        setAiDefs(res.data.scopes || []);
      }
    } catch (err: any) {
      // 403 (scope_admin 未保有) は付与一覧側の案内表示に委ねるため、それ以外のみ通知する
      if (err?.response?.status !== 403) {
        console.error('スコープ定義の取得に失敗しました', err);
        notify.error('スコープ定義の取得に失敗しました。');
      }
    } finally {
      setAiDefsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify は NotificationProvider 側で毎レンダー再生成されるため依存配列には含めない
  }, []);

  // カラム構成の変更履歴（定義と同じタイミングで取得。適用成功後にも再取得する）
  const fetchAiHistory = React.useCallback(async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        ep('ai_scope_view_history'),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        setAiHistory(res.data.history || []);
      }
    } catch (err: any) {
      // 403 (scope_admin 未保有) は付与一覧側の案内表示に委ねる。履歴は補助情報のため通知は出さない
      if (err?.response?.status !== 403) {
        console.error('変更履歴の取得に失敗しました', err);
      }
    }
  }, []);

  useEffect(() => {
    if (activeTab === 0 && contentTab === 1 && !aiDefs && !aiDefsLoading) {
      void fetchAiDefs();
      void fetchAiHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 取得済み/取得中の再実行を防ぐガードがあるため タブ状態のみで発火させる
  }, [activeTab, contentTab, fetchAiDefs, fetchAiHistory]);

  // カラム構成の適用成功後は定義・履歴を取り直し、編集モードを閉じる
  const handleAiViewColumnsApplied = React.useCallback(() => {
    setAiEditingView(null);
    setAiDefs(null);
    void fetchAiDefs();
    void fetchAiHistory();
  }, [fetchAiDefs, fetchAiHistory]);

  // 付与・剥奪の実行（剥奪は参照範囲が狭まる破壊的操作のため確認ダイアログを挟む）
  const handleToggleAiScope = async (scope: AiScopeInfo, granted: boolean) => {
    if (!aiSelectedStaff) return;
    const label = AI_SCOPE_LABELS[scope.scope] ?? scope.scope;
    if (!granted) {
      const ok = await confirm({
        title: 'AI参照スコープの剥奪',
        message: `${aiSelectedStaff.staff_name} さんから「${label}」の参照権限を外しますか？`,
        confirmText: '剥奪する',
        destructive: true,
      });
      if (!ok) return;
    }
    setAiMutatingScope(scope.scope);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        ep(granted ? 'ai_scope_grant' : 'ai_scope_revoke'),
        { user_id: aiSelectedStaff.staff_id, scope: scope.scope },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        notify.success(granted ? `「${label}」を付与しました。` : `「${label}」を剥奪しました。`);
        await fetchAiScopeOverview();
      } else {
        notify.error(res.data.message || 'AI参照スコープの更新に失敗しました。');
      }
    } catch (err: any) {
      notify.error(err?.response?.data?.message || 'AI参照スコープの更新に失敗しました。');
    } finally {
      setAiMutatingScope(null);
    }
  };

  // DB のスタッフ情報と Firestore のユーザー情報をマージ
  const mappedUsers = React.useMemo(() => {
    const dbList = dbStaffList ?? [];
    return firestoreUsers.map((user) => {
      const staff = dbList.find((s) => s.staff_id === user.user_id);
      return {
        ...user,
        name: staff ? staff.staff_name : user.name || '名前未設定',
        department: staff ? staff.department_name : user.department || '',
        branch: staff ? staff.branch_name : user.branch || '',
        joinedcompany_date: staff ? staff.joinedcompany_date : null,
        retirement_date: staff ? staff.retirement_date : null,
        role: staff ? staff.role_name : user.role || '',
      };
    });
  }, [firestoreUsers, dbStaffList]);

  // 選択中のユーザーオブジェクト
  const selectedUser = React.useMemo(() => {
    if (!selectedUserId) return null;
    return mappedUsers.find((user) => user.user_id === selectedUserId) || null;
  }, [selectedUserId, mappedUsers]);

  // --------------------------------------------------
  // Firestore の確定値（selectedUser.permissions）と権限マスタ（allPermissionsState）から
  // containerItems（ローカル表示用の所属列）を作り直すヘルパー。
  // ・selectedUser 切替時 / Firestore側の確定値変化時の再同期
  // ・onDragCancel（Escキー等でのキャンセル）
  // ・over が無いままドロップされた場合
  // ・Firestore 書き込み失敗時のロールバック
  // これら4箇所すべてで共通利用する。
  // --------------------------------------------------
  const buildContainerItemsFromFirestore = React.useCallback((): Record<ContainerKey, string[]> => {
    if (!selectedUser) {
      return { granted: [], available: [] };
    }
    const grantedIdSet = new Set(selectedUser.permissions ?? []);
    const granted: string[] = [];
    const available: string[] = [];
    allPermissionsState.forEach((p) => {
      if (grantedIdSet.has(p.id)) {
        granted.push(p.id);
      } else {
        available.push(p.id);
      }
    });
    return { granted, available };
  }, [selectedUser, allPermissionsState]);

  // selectedUser が切り替わったとき・Firestore側の確定値が変わったときに再同期する
  useEffect(() => {
    setContainerItems(buildContainerItemsFromFirestore());
  }, [buildContainerItemsFromFirestore]);

  // Firestore に未登録のユーザー（差分）
  const diffItems = React.useMemo(() => {
    const firestoreIds = new Set(firestoreUsers.map((u) => u.user_id));
    return dbStaffList.filter((s) => s.staff_id && !firestoreIds.has(s.staff_id));
  }, [dbStaffList, firestoreUsers]);//[] の中身は依存配列→ここが変更された時だけ実行される

  // 新規ユーザーを Firestore に登録
  const handleRegisterUser = async (staff: DbStaff) => {
    setRegisteringId(staff.staff_id);
    try {
      const docRef = doc(db, "HozenallCloud_Permissions", staff.staff_id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        notify.warning(`ユーザー「${staff.staff_name || staff.staff_id}」は既に登録されています。`);
        return;
      }
      await setDoc(docRef, { user_id: staff.staff_id, permissions: [] });
      notify.success(`ユーザー「${staff.staff_name || staff.staff_id}」を登録しました。`);
    } catch (err) {
      console.error('Firestore registration error:', err);
      notify.error('ユーザー登録に失敗しました。');
    } finally {
      setRegisteringId(null);
    }
  };

  // --------------------------------------------------
  // 権限の付与 / 解除（実装済み）
  const handleGrantPermission = async (permissionId: string) => {
    if (!selectedUser) return;
    try {
      const docRef = doc(db, "HozenallCloud_Permissions", selectedUser.id);
      await updateDoc(docRef, { permissions: arrayUnion(permissionId) });
      notify.success(`権限「${permissionId}」を付与しました。`);
    } catch (err) {
      console.error('Grant permission error:', err);
      notify.error('権限付与に失敗しました。');
      // 書き込み失敗時は見た目を Firestore 確定値に戻す（ロールバック）
      setContainerItems(buildContainerItemsFromFirestore());
    }
  };

  const handleRevokePermission = async (permissionId: string) => {
    if (!selectedUser) return;
    try {
      const docRef = doc(db, "HozenallCloud_Permissions", selectedUser.id);
      await updateDoc(docRef, { permissions: arrayRemove(permissionId) });
      notify.success(`権限「${permissionId}」を解除しました。`);
    } catch (err) {
      console.error('Revoke permission error:', err);
      notify.error('権限解除に失敗しました。');
      setContainerItems(buildContainerItemsFromFirestore());
    }
  };

  // --------------------------------------------------
  // ドラッグ & ドロップのハンドリング設定
  // @dnd-kit/core に最初から用意されている。マウスによるドラッグとキーボードによるドラッグを組み合わせている
  // pointer.ts
  // activationConstraint: { distance: 4 } → マウスを4px以上動かすとドラッグが開始される
  // --------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // --------------------------------------------------
  // ドラッグ対象・ドロップ先IDが属するコンテナ（'granted' または 'available'）を判定するヘルパー関数←findContainer
  // 「今マウスで掴んでいるカード（アイテム）は、どっちの箱（コンテナ）に属しているんだっけ？」をプログラム側で判定して箱のIDを返す
  // 1. ドラッグ対象・ドロップ先のIDが 'granted' または 'available' の場合
  // 2. ドラッグ対象・ドロップ先のIDが、付与済み権限リストまたは利用可能権限リストのIDのいずれかである場合
  // 3. それ以外の場合
  // --------------------------------------------------
  const findContainer = (id: string): ContainerKey | null => {
    if (id === 'granted' || id === 'available') {
      return id;
    }
    if (containerItems.granted.includes(id)) {
      return 'granted';
    }
    if (containerItems.available.includes(id)) {
      return 'available';
    }
    return null;
  };

  // --------------------------------------------------
  // ドラッグ開始：DragOverlay に表示するカードの内容を覚えておく
  // --------------------------------------------------
  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id as string;
    setActiveDragPermission(permissionById.get(activeId) ?? null);
  };

  // --------------------------------------------------
  // ドラッグ中：カーソルが元と異なるコンテナ（列）に重なった瞬間、
  // ローカル state 上でアイテムをそのコンテナへ即時に移動させる。
  // ここでは Firestore への書き込みは一切行わない（表示上の楽観的更新のみ）。
  // 同一コンテナ内のホバーは dnd-kit の SortableContext が自動的に
  // 隙間を作ってくれるため何もしない。
  // --------------------------------------------------
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    setContainerItems((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];
      const activeIndex = activeItems.indexOf(activeId);
      if (activeIndex === -1) return prev; // 念のためのガード

      // ドロップ先が「箱」自体（列の空スペース）なら末尾、
      // 個別カード上なら、そのカードの手前に挿入する
      const overIndex = overItems.indexOf(overId);
      const insertAt = overIndex >= 0 ? overIndex : overItems.length;

      return {
        ...prev,
        [activeContainer]: activeItems.filter((id) => id !== activeId),
        [overContainer]: [
          ...overItems.slice(0, insertAt),
          activeId,
          ...overItems.slice(insertAt),
        ],
      };
    });
  };

  // --------------------------------------------------
  // カラム間のドラッグ＆ドロップの確定処理。
  // 「見た目上どちらの列にあるか」ではなく、
  // 「ドラッグ開始前に Firestore 上で実際に付与されていたか」と
  // 「onDragOver 後、最終的にどちらの列に収まったか（ローカル state 基準）」を比較し、
  // 差分がある場合のみ Firestore へ書き込む。
  // （onDragOver で既にローカルの所属列が更新済みのため、
  //   旧来の「activeContainer === overContainer なら何もしない」という判定は
  //   常に成立してしまい使えない点に注意）
  // --------------------------------------------------
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragPermission(null);
    const { active, over } = event;
    const activeId = active.id as string;

    // ドロップ先が無い（列の外にドロップされた等）場合は、
    // onDragOver によるローカルの仮移動を Firestore 確定値に巻き戻す
    if (!over) {
      setContainerItems(buildContainerItemsFromFirestore());
      return;
    }

    const finalContainer = findContainer(activeId); // onDragOver 後の最終的な所属列
    if (!finalContainer) return;

    // ドラッグ開始前に Firestore 上で実際に付与されていたかどうか
    const wasGranted = (selectedUser?.permissions ?? []).includes(activeId);
    const isNowGranted = finalContainer === 'granted';

    // 見た目上の列が変わっていても、最終的に Firestore の確定値と同じなら何もしない
    // （同一列内での並び替え・行ったり来たりして元に戻ったケースを含む）
    if (isNowGranted === wasGranted) {
      return;
    }

    if (isNowGranted) {
      handleGrantPermission(activeId); // 未割当 → 付与済み
    } else {
      handleRevokePermission(activeId); // 付与済み → 未割当
    }
  };

  // --------------------------------------------------
  // ドラッグキャンセル（Esc キー等）：ローカル state を Firestore 確定値に戻す
  // --------------------------------------------------
  const handleDragCancel = (_event: DragCancelEvent) => {
    setActiveDragPermission(null);
    setContainerItems(buildContainerItemsFromFirestore());
  };

  // --------------------------------------------------
  // 選択されたユーザーの付与済み / 利用可能権限リストを算出
  // リアルタイムで「持っているリスト」と「持っていないリスト」に仕分けしている。
  // --------------------------------------------------

  // --------------------------------------------------
  // 表示用：containerItems（id の配列）を Permission オブジェクトに変換する
  // --------------------------------------------------
  const permissionById = React.useMemo(() => {
    const map = new Map<string, Permission>();
    allPermissionsState.forEach((p) => map.set(p.id, p));
    return map;
  }, [allPermissionsState]);

  // 付与済み権限リスト
  const grantedPermissions = React.useMemo(
    () =>
      containerItems.granted
        .map((id) => permissionById.get(id))
        .filter((p): p is Permission => Boolean(p)),
    [containerItems.granted, permissionById]
  );

  // 利用可能権限リスト
  const availablePermissions = React.useMemo(
    () =>
      containerItems.available
        .map((id) => permissionById.get(id))
        .filter((p): p is Permission => Boolean(p)),
    [containerItems.available, permissionById]
  );

  // --------------------------------------------------
  // 権限マスタ編集タブ：クリックボタンの選択肢を1段ずつ計算する処理
  //
  // newPermissionPath には「今までクリックした内容」が順番に入っている。（ボタンの段数を計算する部屋）
  // 例: menu → crmcustomermanagement → view とクリックすると
  //     newPermissionPath = ['menu', 'crmcustomermanagement', 'view'] になる。
  //
  // この処理は、PERMISSION_CANDIDATES（連想配列。78行目付近）を上から順にたどりながら、
  // 「次の段のボタンに何を表示すればよいか」を1段ずつ計算する。
  //   - たどり着いた場所が「配列」→ それが最後の選択肢。選んだらそこで完成、これ以上の段はない
  //   - たどり着いた場所が「オブジェクト（さらに入れ子）」→ まだ続きがあるので、次の段のボタンを用意する
  //
  // この「配列か、まだオブジェクトか」を毎回チェックするだけの仕組みのおかげで、
  // "menu.crmcustomermanagement.view"（3段）も "menu.development.isodocuments.view"（4段）も
  // 同じロジックでそのまま対応できる。
  //options の中に入れているのは、連想配列から「ボタンのラベル（文字）にしたい部分(キー)だけを、
  // 使いやすいようにただの『文字のリスト（配列）』として抜き出したもの」
  const permissionStages = React.useMemo(() => {
    const stages: { options: string[]; selected?: string; isLeaf: boolean }[] = [];
    let node: PermissionNode = PERMISSION_CANDIDATES;
    for (let depth = 0; ; depth++) {
      const isLeaf = Array.isArray(node);
      const options = isLeaf// 条件：isLeaf＝配列ですか？
        ? [...(node as readonly string[])]//「はい（true）」ならこれをする
        : Object.keys(node as Record<string, PermissionNode>);
      const selected = newPermissionPath[depth];
      stages.push({ options, selected, isLeaf });
      if (isLeaf) break; // 配列（終端）まで来たら、これ以上の段は無い
      if (selected === undefined || !(selected in (node as Record<string, PermissionNode>))) break;
      node = (node as Record<string, PermissionNode>)[selected];
    }
    return stages;
  }, [newPermissionPath]);

  // 最後の段が配列由来で、かつ選択済みなら権限文字列は完成している
  const isNewPermissionComplete =
    permissionStages.length > 0 &&
    permissionStages[permissionStages.length - 1].isLeaf &&
    permissionStages[permissionStages.length - 1].selected !== undefined;

  // 指定した段のボタンを選び直した場合、それより後ろの段の選択はリセットする
  const handleSelectPermissionSegment = (depth: number, value: string) => {
    setNewPermissionPath((prev) => [...prev.slice(0, depth), value]);
  };

  // --------------------------------------------------
  // 社員一覧列（クリックで選択、付与済みAIスコープをチップ表示。母集団は DBスタッフ全件）
  const aiColumns = React.useMemo<Column[]>(() => [
    {
      field: 'staff_name',
      headerName: '社員名',
      width: 250,
      renderCell: (row) => {
        const grants = aiGrantsByUser.get(row.staff_id) ?? [];
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.6, cursor: 'pointer' }}
            onClick={() => setSelectedUserId(row.staff_id)}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.3,
                    flexShrink: 0,
                    transition: 'color 0.15s',
                    '.MuiTableRow-root:hover &': { color: '#1976d2' },
                  }}
                  noWrap
                >
                  {row.staff_name || '名前未設定'}
                </Typography>
                {([row.branch_name, row.department_name].filter(Boolean).join(' / ')) && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', lineHeight: 1.3, flexShrink: 0 }} noWrap>
                    {[row.branch_name, row.department_name].filter(Boolean).join(' / ')}
                  </Typography>
                )}
                {row.retirement_date && (
                  <Chip label="退職済み" size="small" sx={{ height: 18, fontSize: '10px', flexShrink: 0 }} />
                )}
                {/* 付与済みスコープを右端にチップ表示（一覧から付与状況を一望できるようにする）。
                    スコープが増えても氏名を潰さないよう、チップ側だけを折り返す */}
                <Box sx={{ ml: 'auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5, rowGap: 0.25, minWidth: 0 }}>
                  {grants.map((g) => (
                    <Chip
                      key={g.scope}
                      label={g.scope}
                      size="small"
                      variant="outlined"
                      color={g.scope === 'personnel' ? 'secondary' : g.scope === 'obc' ? 'success' : g.scope === 'event' ? 'warning' : g.scope === 'crm' ? 'info' : 'primary'}
                      sx={{ height: 18, fontSize: '10px' }}
                    />
                  ))}
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', lineHeight: 1.3 }} noWrap>
                {row.staff_id}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ], [aiGrantsByUser]);

  // /////////////////////////////////////////////////////////////
  // ⏳ レンダリング
  // /////////////////////////////////////////////////////////////

  // 読み込み中の UI 表示（本来のコンテンツと同じ高さでページ領域いっぱいに表示）
  if (firestoreLoading || dbLoading) {
    return <LoadingView message="読み込み中..." height={PAGE_FILL_HEIGHT} />;
  }

  // メインレンダー
  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        height: PAGE_FILL_HEIGHT,
        width: '100%',
        boxSizing: 'border-box'
      }}>
      {/* タブヘッダー */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 'bold',
              color: blueGrey[600],
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
          }}
        >
          <Tab label="社員・権限管理" />
          <Tab label="権限マスタ編集" />
        </Tabs>
      </Box>

      {/* タブコンテンツ */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', gap: 3, flexGrow: 1, minHeight: 0 }}>
          {/* 左側：社員一覧テーブル（手順1=社員の選択。母集団は DBスタッフ全件 + AIスコープチップ）。
              管理対象の切替は右コンテンツ上部のタブで行う（左リストにタブは置かない） */}
          <Box sx={{
            width: '36%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            height: '100%'
          }}>
            {/* 検索 */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              p: 1,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
              flexWrap: 'wrap'
            }}>
              {/* setSearchQuery は setState のため参照が安定しており、StaffSearchBox の memo が効く */}
              <StaffSearchBox onSearch={setSearchQuery} />
              {/* 退職者の表示切替 (検索と横並びのスリム配置)。
                  スイッチ直クリックと領域クリックの二重発火を避けるため、切替は親の onClick に一本化する */}
              <Box
                onClick={() => setShowRetired((v) => !v)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, cursor: 'pointer', pr: 0.5 }}
              >
                <Switch size="small" checked={showRetired} sx={{ pointerEvents: 'none' }} />
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', userSelect: 'none' }}>
                  退職者も表示
                </Typography>
              </Box>
            </Box>
            {/* テーブル */}
            {aiFilteredStaff.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">該当する社員が見つかりません。</Typography>
              </Paper>
            ) : (
              <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <StandardDataTable
                  rows={aiFilteredStaff}
                  columns={aiColumns}
                  totalCount={aiFilteredStaff.length}
                  page={0}
                  pageSize={aiFilteredStaff.length}
                  onPageChange={noopTableHandler}
                  onPageSizeChange={noopTableHandler}
                  onSortChange={noopTableHandler}
                  hidePagination
                />
              </Box>
            )}
          </Box>

          {/* 右側：権限管理 */}
          <Box sx={{
            flex: 1,
            p: 2.5,
            mt: 0,
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden' // 切替アニメーションのスライドがパネル外へはみ出さないようにする
          }}>
            {/* 手順2: 社員の選択は1回のまま、中央コンテンツ側のタブで管理対象を切り替える */}
            <Tabs
              value={contentTab}
              onChange={(_, v) => setContentTab(v)}
              sx={{
                minHeight: 34,
                mb: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': { minHeight: 34, py: 0.25, fontSize: '0.8rem', fontWeight: 'bold', color: blueGrey[500] },
                '& .Mui-selected': { color: 'primary.main' },
              }}
            >
              <Tab label="画面権限" />
              <Tab label="AI参照スコープ" />
            </Tabs>
            {contentTab === 0 ? (
            <AnimatePresence mode="wait" initial={false}>
            {selectedUser ? (
              <motion.div
                key={selectedUser.user_id}
                initial={{ opacity: 0, x: 48 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 48 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}
              >
                {/* Header（高さを抑えるため文字サイズ・余白を圧縮） */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center', mb: 0.5
                }}>
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.4, fontWeight: 700, color: blueGrey[700] }}>
                    {selectedUser.name || '名前未設定'}さんの編集画面
                  </Typography>
                  <Button size="small" onClick={() => setSelectedUserId(null)} sx={{ ...STANDARD_TEXT_BUTTON_SX, py: 0.25, minHeight: 0 }}>閉じる</Button>
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                {/* 権限パネル（ドラッグ対象は 1 つ of DndContext で管理）
                    * dnd-kit というライブラリが提供する DndContext というコンポーネントで、ページ全体を「ドラッグできる領域」にします。
                    * この箱の中に入っている要素は全部 ドラッグ対象 と認識されます。 
                    * <DndContext
                    * sensors={sensors}                     // ドラッグの受付方法 (マウス、キーボード等)
                    * collisionDetection={closestCenter}    // 判定方法 (中心が近いか)@dnd-kit/core が提供している関数
                    * onDragEnd={handleDragEnd}             // ドラッグ終了時の処理
                */}

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, minHeight: 0 }}>
                    {/* 左カラム：付与済み */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: 0,
                        minWidth: 0
                      }}>
                      <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 600, color: blueGrey[500] }}>Permission Assignment</Typography>
                      <SortableContext
                        id="granted"
                        items={containerItems.granted}   // 必須：対象 ID 配列
                        strategy={verticalListSortingStrategy}
                      >
                        <DroppableArea id="granted">
                          {grantedPermissions.map((perm) => (
                            <PermissionSortableCard key={perm.id} permission={perm} />
                          ))}
                        </DroppableArea>
                      </SortableContext>
                    </Paper>

                    {/* 右カラム：利用可能 */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: 0,
                        minWidth: 0
                      }}>
                      <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 600, color: blueGrey[500] }}>Role Permission</Typography>
                      <SortableContext
                        id="available"
                        items={containerItems.available}
                        strategy={verticalListSortingStrategy}
                      >
                        <DroppableArea id="available">
                          {availablePermissions.map((perm) => (
                            <PermissionSortableCard key={perm.id} permission={perm} />
                          ))}
                        </DroppableArea>
                      </SortableContext>
                    </Paper>
                  </Box>

                  {/* ドラッグ中のカードの見た目（画面最前面に表示され、列内の重なり順の影響を受けない）。
                      DragOverlay は position: fixed で位置を決めるが、祖先に backdropFilter や
                      transform があると fixed の基準が画面全体からその祖先へすり替わり、
                      カーソルと位置がずれてしまう（この画面の親 Box は backdropFilter を持つ）。
                      Portal で document.body 直下へ描画し、祖先の影響を受けないようにする。 */}
                  <Portal>
                    <DragOverlay>
                      {activeDragPermission ? (
                        <Paper sx={{ ...cardSx, display: 'flex', alignItems: 'center', boxShadow: 6, cursor: 'grabbing' }}>
                          <Icons.DragIndicator sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} />
                          <Typography variant="body2">{activeDragPermission.name}</Typography>
                        </Paper>
                      ) : null}
                    </DragOverlay>
                  </Portal>
                </DndContext>
              </motion.div>
            ) : aiSelectedStaff ? (
              // 選択済みだが Firestore 未登録の社員 (AI側リストから選択したケース)。
              // その社員だけの登録カードを表示し、登録が済むと onSnapshot 経由で自動的に編集画面へ切り替わる
              <motion.div
                key={`unregistered-${aiSelectedStaff.staff_id}`}
                initial={{ opacity: 0, x: 48 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 48 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.4, fontWeight: 700, color: blueGrey[700] }}>
                    {aiSelectedStaff.staff_name || '名前未設定'} さんの画面権限
                  </Typography>
                  <Button size="small" onClick={() => setSelectedUserId(null)} sx={{ ...STANDARD_TEXT_BUTTON_SX, py: 0.25, minHeight: 0 }}>閉じる</Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', mb: 1 }}>
                  {aiSelectedStaff.staff_id}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Alert severity="info" sx={{ mb: 2 }}>
                  この社員はまだ画面権限の管理に登録されていません。登録すると権限の付与を編集できます。
                </Alert>
                {registeringId ? (
                  <LoadingView message="登録中..." sx={{ flexGrow: 1 }} />
                ) : (
                  <Paper elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      height: 70,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: blueGrey[800] }}>{aiSelectedStaff.staff_name || '名前未設定'}</Typography>
                        {aiSelectedStaff.branch_name && (
                          <Typography component="span" variant="caption" color="text.secondary">{aiSelectedStaff.branch_name} {aiSelectedStaff.department_name}</Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{aiSelectedStaff.staff_id}{aiSelectedStaff.joinedcompany_date ? ` / ${aiSelectedStaff.joinedcompany_date}` : ''}</Typography>
                    </Box>
                    <Button variant="contained" onClick={() => handleRegisterUser(aiSelectedStaff)} sx={{ ...STANDARD_CTA_BUTTON_SX, minWidth: 80, px: 2 }} size="small">
                      登録
                    </Button>
                  </Paper>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="unregistered-list"
                initial={{ opacity: 0, x: 48 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 48 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
              >
                {/* 高さを抑えるため編集画面ヘッダーと同じ文字サイズ・余白に揃える */}
                <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.4, fontWeight: 700, color: blueGrey[500], display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  新規登録一覧 ({diffItems.length} 件)
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {diffItems.length === 0 ? (
                  <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary" align="center">該当する社員はいません。</Typography>
                  </Box>
                ) : registeringId ? (
                  <LoadingView message="登録中..." sx={{ flexGrow: 1 }} />
                ) : (
                  <Box sx={{
                    overflowY: 'auto',
                    pr: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flexGrow: 1,
                    pb: 2
                  }}>
                    {diffItems.map((staff) => (
                      <Paper key={staff.staff_id} elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid rgba(0, 0, 0, 0.05)',
                          height: 70,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'box-shadow 0.2s',
                          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                        }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: blueGrey[800] }}>{staff.staff_name || '名前未設定'}</Typography>
                            {staff.branch_name && (
                              <Typography component="span" variant="caption" color="text.secondary">{staff.branch_name} {staff.department_name}</Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{staff.staff_id}{staff.joinedcompany_date ? ` / ${staff.joinedcompany_date}` : ''}</Typography>
                        </Box>
                        <Button variant="contained" onClick={() => handleRegisterUser(staff)} sx={{ ...STANDARD_CTA_BUTTON_SX, minWidth: 80, px: 2 }} size="small">
                          登録
                        </Button>
                      </Paper>
                    ))}
                  </Box>
                )}
              </motion.div>
            )}
            </AnimatePresence>
            ) : aiScopeForbidden ? (
              <Alert severity="warning">
                AI参照スコープの管理権限（scope_admin）が付与されていないため、この管理対象は操作できません。
                システム管理者へ権限付与を依頼してください。
              </Alert>
            ) : (
              <AnimatePresence mode="wait" initial={false}>
                {aiSelectedStaff ? (
                  <motion.div
                    key={aiSelectedStaff.staff_id}
                    initial={{ opacity: 0, x: 48 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 48 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.4, fontWeight: 700, color: blueGrey[700] }}>
                        {aiSelectedStaff.staff_name} さんの AI 参照スコープ
                      </Typography>
                      <Button size="small" onClick={() => setSelectedUserId(null)} sx={{ ...STANDARD_TEXT_BUTTON_SX, py: 0.25, minHeight: 0 }}>閉じる</Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', mb: 1 }}>
                      {aiSelectedStaff.staff_id}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Alert severity="info" sx={{ mb: 2 }}>
                      付与したスコープの範囲で、AI エージェントが社内データベースを参照できるようになります。
                      各スコープに含まれるカラムの構成は社員未選択時の「スコープ定義」から変更できます。
                    </Alert>
                    {aiScopeLoading && !aiScopeLoaded ? (
                      <LoadingView message="AI参照スコープを読み込み中..." height="30vh" size={40} />
                    ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', minHeight: 0 }}>
                      {aiScopes.map((sc) => {
                        const grant = (aiGrantsByUser.get(aiSelectedStaff.staff_id) ?? []).find((g) => g.scope === sc.scope);
                        return (
                          <Paper
                            key={sc.scope}
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid rgba(0,0,0,0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              bgcolor: grant ? 'rgba(25,118,210,0.04)' : 'background.paper',
                            }}
                          >
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {AI_SCOPE_LABELS[sc.scope] ?? sc.scope}
                                </Typography>
                                <Chip label={sc.schema} size="small" sx={{ height: 18, fontSize: '10px', fontFamily: 'monospace' }} />
                              </Box>
                              {sc.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {sc.description}
                                </Typography>
                              )}
                              {grant && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                  付与 {String(grant.granted_at ?? '').slice(0, 16)} / {grant.granted_by}
                                </Typography>
                              )}
                            </Box>
                            <Switch
                              checked={Boolean(grant)}
                              disabled={aiMutatingScope !== null}
                              onChange={(_, checked) => void handleToggleAiScope(sc, checked)}
                            />
                          </Paper>
                        );
                      })}
                    </Box>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="ai-scope-definitions"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}
                  >
                    {/* 社員未選択時はスコープ定義（読み取り専用）を常時表示し、空きスペースを情報に充てる */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <Icons.TableChart sx={{ color: blueGrey[400], fontSize: 18 }} />
                      <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.4, fontWeight: 700, color: blueGrey[700] }}>
                        AI参照スコープの定義（読み取り専用）
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                      左の一覧から社員を選択すると、その社員への付与編集に切り替わります。
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    {aiDefsLoading || !aiDefs ? (
                      <LoadingView message="スコープ定義を読み込み中..." height="40vh" size={40} />
                    ) : (
                      <Box sx={{ overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          AI が参照できるのは各ビューの「公開カラム」のみです。「非公開カラム」は
                          ビュー定義に含まれておらず、AI からは存在自体が見えません。
                          カラム構成の変更はマイグレーション（Git 管理）で行います。
                        </Alert>
                        {aiDefs.map((sc) => (
                    <Box key={sc.scope} sx={{ mb: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: blueGrey[600] }}>
                          {AI_SCOPE_LABELS[sc.scope] ?? sc.scope}
                        </Typography>
                        <Chip label={sc.schema} size="small" sx={{ height: 18, fontSize: '10px', fontFamily: 'monospace' }} />
                      </Box>
                      {sc.views.map((v) => (
                        <Accordion
                          key={v.view}
                          disableGutters
                          elevation={0}
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 0.75,
                            bgcolor: '#fff',
                            '&:before': { display: 'none' },
                            overflow: 'hidden',
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<Icons.ExpandMore sx={{ fontSize: 18 }} />}
                            sx={{
                              minHeight: 0,
                              px: 1,
                              '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center', gap: 0.75 },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                              {v.view}
                            </Typography>
                            <Chip label={`公開 ${v.columns.length}列`} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '10px' }} />
                            {v.base_tables.map((bt) => (
                              <Chip
                                key={bt.table}
                                label={`非公開 ${bt.hidden_columns.length}列`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '10px' }}
                              />
                            ))}
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 1.5, pt: 1, pb: 1.5, borderTop: 1, borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                                {v.description}
                              </Typography>
                              {v.editable && v.base_tables[0]?.all_columns && aiEditingView !== v.view && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Icons.Edit sx={{ fontSize: 14 }} />}
                                  onClick={() => setAiEditingView(v.view)}
                                  sx={{ flexShrink: 0, py: 0, fontSize: '0.72rem' }}
                                >
                                  カラム構成を編集
                                </Button>
                              )}
                            </Box>

                            {aiEditingView === v.view ? (
                              <AiViewColumnEditor
                                def={v}
                                onCancel={() => setAiEditingView(null)}
                                onApplied={handleAiViewColumnsApplied}
                              />
                            ) : (
                            <>
                            {/* 公開カラム一覧（コメント＝データ辞書付き） */}
                            <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[600], display: 'block', mb: 0.5 }}>
                              公開カラム（{v.columns.length}）
                            </Typography>
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
                              {v.columns.map((c, i) => (
                                <Box
                                  key={c.name}
                                  sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    px: 1,
                                    py: 0.4,
                                    bgcolor: i % 2 === 0 ? 'background.paper' : 'rgba(0,0,0,0.02)',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', width: 220, flexShrink: 0 }} noWrap>
                                    {c.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ width: 120, flexShrink: 0 }} noWrap>
                                    {c.type}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {c.description}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>

                            {/* 非公開カラム（参照元テーブルにあるがビューに出していない列） */}
                            {v.base_tables.map((bt) => (
                              <Box key={bt.table} sx={{ mb: 1.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[600], display: 'block', mb: 0.5 }}>
                                  非公開カラム（{bt.hidden_columns.length}）— 参照元 {bt.table}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {bt.hidden_columns.map((c) => (
                                    <Tooltip
                                      key={c.name}
                                      title={c.permanent ? `恒久非公開 — ${c.description || c.type}` : (c.description || c.type)}
                                    >
                                      <Chip
                                        label={c.name}
                                        size="small"
                                        variant="outlined"
                                        icon={c.permanent ? <Icons.Lock sx={{ fontSize: 11 }} /> : undefined}
                                        sx={{ height: 20, fontSize: '10px', fontFamily: 'monospace', color: 'text.secondary' }}
                                      />
                                    </Tooltip>
                                  ))}
                                </Box>
                              </Box>
                            ))}

                            {/* 稼働中のビュー定義（SELECT / WHERE の現物） */}
                            <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[600], display: 'block', mb: 0.5 }}>
                              稼働中のビュー定義（SQL）
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                p: 1.5,
                                bgcolor: '#263238',
                                color: '#eceff1',
                                borderRadius: 1,
                                fontSize: '0.72rem',
                                fontFamily: 'monospace',
                                lineHeight: 1.5,
                                overflowX: 'auto',
                                whiteSpace: 'pre',
                              }}
                            >
                              {v.definition}
                            </Box>
                            </>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                        ))}

                        {/* カラム構成の変更履歴（trn_ai_view_change_history。適用のたびに追記される監査ログ） */}
                        {aiHistory && aiHistory.length > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: '#fff',
                              '&:before': { display: 'none' },
                              overflow: 'hidden',
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<Icons.ExpandMore sx={{ fontSize: 18 }} />}
                              sx={{
                                minHeight: 0,
                                px: 1,
                                '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center', gap: 0.75 },
                              }}
                            >
                              <Icons.History sx={{ fontSize: 16, color: blueGrey[400] }} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                カラム構成の変更履歴
                              </Typography>
                              <Chip label={`${aiHistory.length}件`} size="small" variant="outlined" sx={{ height: 18, fontSize: '10px' }} />
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 1.5, pt: 1, pb: 1.5, borderTop: 1, borderColor: 'divider' }}>
                              {aiHistory.map((h, i) => (
                                <Box
                                  key={`${h.changed_at}-${i}`}
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1,
                                    py: 0.4,
                                    bgcolor: i % 2 === 0 ? 'background.paper' : 'rgba(0,0,0,0.02)',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', width: 130, flexShrink: 0 }} noWrap>
                                    {String(h.changed_at ?? '').slice(0, 16)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', width: 170, flexShrink: 0 }} noWrap>
                                    {h.view}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flexGrow: 1 }}>
                                    {h.added && h.added.split(',').map((n) => n.trim()).filter(Boolean).map((n) => (
                                      <Chip key={`a-${n}`} label={`+ ${n}`} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '10px', fontFamily: 'monospace' }} />
                                    ))}
                                    {h.removed && h.removed.split(',').map((n) => n.trim()).filter(Boolean).map((n) => (
                                      <Chip key={`r-${n}`} label={`− ${n}`} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: '10px', fontFamily: 'monospace' }} />
                                    ))}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {h.changed_by}
                                  </Typography>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{
          flex: 1,
          p: 3,
          background: 'rgba(255,255,255,0.15)',
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          minHeight: 0,
        }}>
          {/* 左カラム：権限一覧（仮） */}
          <Box sx={{
            flex: 1,
            p: 2,
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
            minHeight: 0,
            overflowY: 'auto',
          }}>
          </Box>

          {/* 右カラム：新規権限の作成（クリックで段階的に選択） */}
          <Box sx={{
            flex: 1,
            p: 2,
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
            minHeight: 0,
            overflowY: 'auto',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: blueGrey[500], mb: 2 }}>
              新規権限の作成
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {permissionStages.map((stage, depth) => (
                <Box key={depth}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {depth + 1}段目
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={stage.selected ?? null}
                    onChange={(_, value) => value && handleSelectPermissionSegment(depth, value)}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {stage.options.map((opt) => (
                      <ToggleButton key={opt} value={opt} sx={{ borderRadius: '8px !important', border: '1px solid rgba(0,0,0,0.12) !important' }}>
                        {opt}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              ))}
            </Box>

            {/* プレビュー */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" color="text.secondary">プレビュー</Typography>
              <Typography variant="h6" sx={{ fontFamily: 'monospace', color: isNewPermissionComplete ? blueGrey[800] : 'text.disabled' }}>
                {newPermissionPath.length > 0 ? newPermissionPath.join('.') : '（まだ選択されていません）'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
